import React, { useEffect } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useSite } from "@/lib/SiteContext";
import { ArrowUpRight, ShieldCheck } from "lucide-react";

export default function Login() {
    const { user, site } = useSite();
    const navigate = useNavigate();
    const location = useLocation();
    const error = new URLSearchParams(location.search).get("error");

    useEffect(() => {
        if (user) navigate("/admin", { replace: true });
    }, [user, navigate]);

    const handleGoogle = () => {
        // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
        const redirectUrl = window.location.origin + "/admin";
        window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
    };

    return (
        <div className="min-h-screen bg-ink-900 text-white relative overflow-hidden">
            <div className="absolute inset-0 grid-lines opacity-30 pointer-events-none" />
            <div className="noise" />
            <div className="mx-auto max-w-[1400px] px-5 md:px-10 min-h-screen grid grid-cols-1 lg:grid-cols-2 items-center gap-10 py-16">
                {/* Left */}
                <div>
                    <Link to="/" data-testid="login-back" className="font-mono-label text-white/60 hover:text-lime-signal inline-flex items-center gap-2">
                        ← Back to site
                    </Link>
                    <h1 className="mt-8 font-display uppercase leading-[0.85] text-6xl md:text-8xl">
                        <span className="text-white">Admin</span>
                        <br />
                        <span className="text-lime-signal">access.</span>
                    </h1>
                    <p className="mt-6 text-white/60 max-w-md leading-relaxed">
                        Sign in with your Google account to update content, upload images and manage quote requests.
                    </p>

                    <ul className="mt-10 space-y-3 font-mono-label text-white/70">
                        <li className="flex items-center gap-2"><ShieldCheck size={14} className="text-lime-signal" /> Google OAuth · encrypted session</li>
                        <li className="flex items-center gap-2"><ShieldCheck size={14} className="text-lime-signal" /> First login becomes owner-admin</li>
                        <li className="flex items-center gap-2"><ShieldCheck size={14} className="text-lime-signal" /> Only admin roles can edit</li>
                    </ul>
                </div>

                {/* Right */}
                <div className="card-tactical p-8 md:p-12">
                    {site?.logo_url && (
                        <img src={site.logo_url} alt={site.business_name} className="w-24 h-24 mb-8" />
                    )}
                    <div className="font-mono-label text-lime-signal mb-3">Continue with</div>
                    <button
                        onClick={handleGoogle}
                        data-testid="login-google-btn"
                        className="w-full flex items-center justify-between px-6 py-5 bg-white text-ink-900 rounded-xl font-bold hover:bg-lime-signal transition-colors group"
                    >
                        <span className="flex items-center gap-3">
                            <GoogleIcon />
                            Sign in with Google
                        </span>
                        <ArrowUpRight size={18} className="group-hover:translate-x-1 group-hover:-translate-y-1 transition-transform" />
                    </button>
                    {error && (
                        <p className="mt-4 text-red-400 text-sm font-mono-label">
                            Sign-in failed. Please try again.
                        </p>
                    )}
                    <p className="mt-6 text-xs text-white/40 leading-relaxed">
                        By continuing you agree to responsible use of the admin panel. Sessions expire after 7 days.
                    </p>
                </div>
            </div>
        </div>
    );
}

function GoogleIcon() {
    return (
        <svg viewBox="0 0 24 24" width="20" height="20" aria-hidden>
            <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.75h3.57c2.08-1.92 3.28-4.74 3.28-8.07z" />
            <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.75c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" />
            <path fill="#FBBC05" d="M5.84 14.11a6.98 6.98 0 010-4.22V7.05H2.18a11 11 0 000 9.9l3.66-2.84z" />
            <path fill="#EA4335" d="M12 5.38c1.61 0 3.06.55 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.05l3.66 2.84C6.71 7.31 9.14 5.38 12 5.38z" />
        </svg>
    );
}
